import { useState } from "react";
import { useLocation, useSearch } from "wouter";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import productionHubLogo from "@assets/Production_Hub_1771339483036.png";
import bgVideo from "@assets/Production_1771479229515.mp4";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isPending, setIsPending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const search = useSearch();
  const token = new URLSearchParams(search).get("token");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword.trim() || !confirmPassword.trim()) return;

    if (newPassword !== confirmPassword) {
      toast({ title: "Passwords don't match", description: "Please make sure both passwords match.", variant: "destructive", severity: "error" });
      return;
    }

    setIsPending(true);
    try {
      const res = await apiRequest("POST", "/api/auth/reset-password", { token, newPassword });
      const data = await res.json();
      setIsSuccess(true);
      toast({ title: "Password reset", description: data.message });
    } catch (err: any) {
      let msg = "Failed to reset password. Please try again.";
      try {
        const text = err.message || "";
        const match = text.match(/\d+: (.*)/);
        if (match) {
          const parsed = JSON.parse(match[1]);
          msg = parsed.message || msg;
        }
      } catch {}
      toast({ title: "Reset failed", description: msg, variant: "destructive", severity: "error" });
    } finally {
      setIsPending(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ backgroundColor: "hsl(var(--brand-dark))" }}>
        <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.9 }}>
          <source src={bgVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 54, 58, 0.55)" }} />
        <div className="w-full max-w-sm px-4 relative z-10">
          <Card style={{ backgroundColor: "hsl(var(--brand-dark))", border: "none" }}>
            <CardHeader className="pb-4 flex flex-col items-center gap-4">
              <img src={productionHubLogo} alt="Prodigy Production Hub" className="w-auto" style={{ height: "3.85rem" }} />
              <h2 className="text-lg font-semibold text-center text-white">Invalid Reset Link</h2>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-sm text-gray-300 mb-4">This password reset link is invalid. Please request a new one from the sign-in page.</p>
              <Button variant="outline" onClick={() => navigate("/", { replace: true })} className="w-full">
                Back to Sign In
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ backgroundColor: "hsl(var(--brand-dark))" }}>
        <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.9 }}>
          <source src={bgVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 54, 58, 0.55)" }} />
        <div className="w-full max-w-sm px-4 relative z-10">
          <Card style={{ backgroundColor: "hsl(var(--brand-dark))", border: "none" }}>
            <CardHeader className="pb-4 flex flex-col items-center gap-4">
              <img src={productionHubLogo} alt="Prodigy Production Hub" className="w-auto" style={{ height: "3.85rem" }} />
              <CheckCircle2 className="h-12 w-12 text-[hsl(var(--brand-green))]" />
              <h2 className="text-lg font-semibold text-center text-white">Password Reset</h2>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-sm text-gray-300 mb-4">Your password has been reset successfully. You can now sign in with your new password.</p>
              <Button onClick={() => navigate("/", { replace: true })} className="w-full">
                Sign In
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ backgroundColor: "hsl(var(--brand-dark))" }}>
      <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.9 }}>
        <source src={bgVideo} type="video/mp4" />
      </video>
      <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 54, 58, 0.55)" }} />
      <div className="w-full max-w-sm px-4 relative z-10">
        <Card style={{ backgroundColor: "hsl(var(--brand-dark))", border: "none" }}>
          <CardHeader className="pb-4 flex flex-col items-center gap-4">
            <img src={productionHubLogo} alt="Prodigy Production Hub" className="w-auto" style={{ height: "3.85rem" }} />
            <h2 className="text-lg font-semibold text-center text-white">Set New Password</h2>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-white">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-white">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={isPending || !newPassword.trim() || !confirmPassword.trim()}
              >
                {isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  "Reset Password"
                )}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate("/", { replace: true })}
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
